import { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertTriangle, CheckCircle, Camera, Truck, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BiancheriaPage = () => {
  const [formData, setFormData] = useState({
    establishmentName: '',
    contactPerson: '',
    email: '',
    phone: '',
    bedCount: '',
    frequency: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact/biancheria`, formData);
      setIsSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=1600&q=80)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 to-white/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-primary font-medium text-sm tracking-widest uppercase mb-4 block">
              Servizio Lavanderia Biancheria
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary mb-6 leading-tight">
              Riprenditi il <span className="text-primary">tempo</span> che i problemi ti portano via
            </h1>
            <p className="text-xl text-text-secondary mb-8">
              Delega completamente la gestione della biancheria del tuo B&B a Kilolava
            </p>
            <Button 
              data-testid="biancheria-hero-cta"
              size="lg"
              className="bg-primary hover:bg-primary-hover text-white px-8 py-6 text-lg rounded-full shadow-[0_20px_50px_rgb(233,30,99,0.3)]"
              onClick={() => document.getElementById('contact-biancheria')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Richiedi Informazioni sul Servizio
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary mb-6">
              Quanto tempo perdi dietro la gestione di ogni appartamento?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              {
                icon: AlertTriangle,
                title: "La staff delle pulizie ti ha paccato",
                description: "E hai dovuto pulire tu l'appartamento"
              },
              {
                icon: Clock,
                title: "Il frigorifero è rimasto sporco",
                description: "E sei dovuto correre dall'ospite con un regalo per scusarti"
              },
              {
                icon: AlertTriangle,
                title: "Lenzuola con macchia gialla",
                description: "E solo tu sai come sei riuscito ad evitare la recensione negativa"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-red-50 border border-red-200 rounded-2xl p-6"
              >
                <item.icon className="w-10 h-10 text-red-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-text-primary mb-2">{item.title}</h3>
                <p className="text-text-secondary">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gray-100 rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto"
          >
            <p className="text-lg md:text-xl text-text-secondary leading-relaxed mb-6">
              Per un motivo o per un altro perdi sempre una marea di tempo.
              <br /><br />
              Tempo che potresti usare per <strong>far crescere la tua attività</strong> o per goderti i soldi che il tuo impegno ti sta fruttando.
              <br /><br />
              Tempo che ti viene rubato dallo <strong className="text-primary">staff delle pulizie che non ti dà sicurezza</strong>
            </p>
            <p className="text-text-secondary italic">
              Ma questo è normale, non sono "cattivi". Semplicemente nessuno gli ha dato delle procedure da seguire, 
              nessuno gli ha dato un metodo di lavoro chiaro, nessuno gli ha insegnato ad attenersi rigorosamente a tutto ciò.
              <br /><br />
              E di conseguenza tu devi risolvere i loro "casini"
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8">
              Questo è esattamente il motivo per cui
            </h2>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              <div className="text-center">
                <div className="font-heading text-6xl md:text-8xl font-bold">50+</div>
                <div className="text-white/80">Persone</div>
              </div>
              <div className="text-4xl">con</div>
              <div className="text-center">
                <div className="font-heading text-6xl md:text-8xl font-bold">150+</div>
                <div className="text-white/80">Case Vacanza</div>
              </div>
            </div>
            <p className="text-xl mt-8 text-white/90">
              hanno deciso di affidarsi a <strong>Kilolava</strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary">
              Cosa offriamo ai nostri clienti
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: Truck,
                title: "Biancheria Pulita",
                description: "Consegnata direttamente in appartamento"
              },
              {
                icon: CheckCircle,
                title: "Procedure Personalizzate",
                description: "Chiare e su misura per pulire alla perfezione ogni appartamento"
              },
              {
                icon: Camera,
                title: "Foto e Video",
                description: "Per poter assicurare il miglior risultato ogni volta"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold text-text-primary mb-2">{item.title}</h3>
                <p className="text-text-secondary">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-xl text-text-secondary mb-8">
              In questo modo non avrai più quel pensiero costante ad ogni check in<br />
              e potrai riuscire a <strong className="text-primary">riprendere il controllo del tuo tempo</strong>
            </p>
            <Button 
              data-testid="biancheria-mid-cta"
              size="lg"
              className="bg-primary hover:bg-primary-hover text-white px-8 py-6 text-lg rounded-full"
              onClick={() => document.getElementById('contact-biancheria')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Richiedi Informazioni sul Servizio
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Rental Bonus */}
      <section className="py-20 bg-text-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-8">
              <Gift className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              E la cosa bella?
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Con Kilolava puoi anche <strong className="text-primary">Noleggiare la biancheria</strong>,
              in modo da diminuire notevolmente il costo di avviamento per le tue strutture 
              o quelle dei tuoi clienti e rientrare dell'investimento più velocemente
            </p>
            <Button 
              data-testid="biancheria-rental-cta"
              size="lg"
              className="bg-primary hover:bg-primary-hover text-white px-8 py-6 text-lg rounded-full"
              onClick={() => document.getElementById('contact-biancheria')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Richiedi Informazioni sul Servizio
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-biancheria" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary mb-4">
              Richiedi Informazioni per <span className="text-primary">Biancheria</span>
            </h2>
            <p className="text-text-secondary">Compila il modulo e ti ricontatteremo entro 24 ore</p>
          </motion.div>

          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-primary/5 border border-primary/20 rounded-3xl p-12 text-center"
            >
              <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
              <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">
                Richiesta Inviata!
              </h3>
              <p className="text-text-secondary">Ti ricontatteremo presto.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 bg-primary/5 rounded-3xl p-8 border border-primary/10">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="establishmentName">Nome Struttura *</Label>
                  <Input
                    data-testid="biancheria-form-establishment"
                    id="establishmentName"
                    name="establishmentName"
                    value={formData.establishmentName}
                    onChange={handleChange}
                    required
                    placeholder="Es. B&B Vista Mare"
                    className="h-12 bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactPerson">Referente *</Label>
                  <Input
                    data-testid="biancheria-form-contact"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                    placeholder="Mario Rossi"
                    className="h-12 bg-white"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    data-testid="biancheria-form-email"
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="email@esempio.com"
                    className="h-12 bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono *</Label>
                  <Input
                    data-testid="biancheria-form-phone"
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+39 333 1234567"
                    className="h-12 bg-white"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="bedCount">Numero Letti (circa)</Label>
                  <Input
                    data-testid="biancheria-form-beds"
                    id="bedCount"
                    name="bedCount"
                    value={formData.bedCount}
                    onChange={handleChange}
                    placeholder="Es. 20"
                    className="h-12 bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="frequency">Frequenza Servizio</Label>
                  <Select onValueChange={(value) => setFormData({...formData, frequency: value})}>
                    <SelectTrigger data-testid="biancheria-form-frequency" className="h-12 bg-white">
                      <SelectValue placeholder="Seleziona frequenza" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="giornaliera">Giornaliera</SelectItem>
                      <SelectItem value="settimanale">Settimanale</SelectItem>
                      <SelectItem value="bisettimanale">Bisettimanale</SelectItem>
                      <SelectItem value="mensile">Mensile</SelectItem>
                      <SelectItem value="su-richiesta">Su Richiesta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Messaggio</Label>
                <Textarea
                  data-testid="biancheria-form-message"
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Raccontaci le tue esigenze..."
                  rows={4}
                  className="bg-white"
                />
              </div>

              <Button
                data-testid="biancheria-form-submit"
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary-hover text-white py-6 text-lg rounded-full"
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia Richiesta'}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default BiancheriaPage;
